/**
 * Analytics form-effect — injects analytics scripts into the document.
 * Supports Google Tag Manager, Google Analytics, Facebook Pixel,
 * LinkedIn Insight Tag, and UTM parameter capture.
 * SSR-safe: returns undefined if document is not available.
 */

import type { FormEffectHandler } from '@atlas-forms/types-js';
import { str, track } from './utils';

export const analyticsEffect: FormEffectHandler = (config) => {
  if (typeof document === 'undefined') return;

  const created: Element[] = [];

  // Google Tag Manager
  const gtmId = str(config.googleTagManagerId);
  if (gtmId) {
    const script = document.createElement('script');
    script.setAttribute('data-atlas-analytics', 'gtm');
    script.textContent = [
      `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':`,
      `new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],`,
      `j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=`,
      `'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);`,
      `})(window,document,'script','dataLayer','${gtmId}');`,
    ].join('');
    document.head.appendChild(track(script, created));
  }

  // Google Analytics (gtag.js)
  const gaId = str(config.googleAnalyticsId);
  if (gaId) {
    // Async gtag.js loader script
    const loaderScript = document.createElement('script');
    loaderScript.setAttribute('data-atlas-analytics', 'ga-loader');
    loaderScript.async = true;
    loaderScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(track(loaderScript, created));

    // Inline init script
    const initScript = document.createElement('script');
    initScript.setAttribute('data-atlas-analytics', 'ga-init');
    initScript.textContent = [
      `window.dataLayer = window.dataLayer || [];`,
      `function gtag(){dataLayer.push(arguments);}`,
      `gtag('js', new Date());`,
      `gtag('config', '${gaId}');`,
    ].join('\n');
    document.head.appendChild(track(initScript, created));
  }

  // Facebook Pixel
  const fbPixelId = str(config.facebookPixelId);
  if (fbPixelId) {
    const fbScript = document.createElement('script');
    fbScript.setAttribute('data-atlas-analytics', 'fb-pixel');
    fbScript.textContent = [
      `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){`,
      `n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};`,
      `if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';`,
      `n.queue=[];t=b.createElement(e);t.async=!0;`,
      `t.src=v;s=b.getElementsByTagName(e)[0];`,
      `s.parentNode.insertBefore(t,s)}(window, document,'script',`,
      `'https://connect.facebook.net/en_US/fbevents.js');`,
      `fbq('init', '${fbPixelId}');`,
      `fbq('track', 'PageView');`,
    ].join('');
    document.head.appendChild(track(fbScript, created));
  }

  // LinkedIn Insight Tag
  const liTagId = str(config.linkedInInsightTagId);
  if (liTagId) {
    const liScript = document.createElement('script');
    liScript.setAttribute('data-atlas-analytics', 'linkedin-insight');
    liScript.textContent = [
      `_linkedin_partner_id = "${liTagId}";`,
      `window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];`,
      `window._linkedin_data_partner_ids.push(_linkedin_partner_id);`,
      `(function(l) {`,
      `if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};`,
      `window.lintrk.q=[]}`,
      `var s = document.getElementsByTagName("script")[0];`,
      `var b = document.createElement("script");`,
      `b.type = "text/javascript";b.async = true;`,
      `b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";`,
      `s.parentNode.insertBefore(b, s);})(window.lintrk);`,
    ].join('\n');
    document.head.appendChild(track(liScript, created));
  }

  // UTM parameter capture
  if (config.utmCaptureEnabled === true) {
    try {
      const params = new URLSearchParams(window.location.search);
      const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
      const utmData: Record<string, string> = {};
      for (const key of utmKeys) {
        const val = params.get(key);
        if (val && val.trim()) {
          utmData[key] = val.trim();
        }
      }
      if (Object.keys(utmData).length > 0) {
        sessionStorage.setItem('atlas_forms_utm', JSON.stringify(utmData));
      }
    } catch {
      // sessionStorage may be unavailable in certain environments
    }
  }

  return () => {
    for (const el of created) {
      el.parentNode?.removeChild(el);
    }
  };
};
